import type { RegisteredEditorModule } from '../../types';
import {
  getCrosswordSource,
  createCrossword,
  replaceCrossword,
  previewCrossword,
  duplicatePassatempo,
  isCrosswordTipo,
} from '../../../../../services/admin';
import { createEmpty, type CrosswordDoc } from './doc';
import { crosswordReducer, type CrosswordAction } from './reducer';
import { validate } from './validate';
import { deriveIndices } from './deriveIndices';
import { coalesceKeyFor, initialCrosswordUi } from './ui';
import { docToDraftPayload, draftToDoc, sourceToDoc } from './wire';
import { Canvas } from './Canvas';
import { Inspector } from './Inspector';

// Monta o EditorModule da cruzada direta: o núcleo puro + os serviços (via wire) + a
// UI. O shell só conhece esta abstração.

// ATENÇÃO: são duas tabelas diferentes que por coincidência usam o id 3.
// `estilos_de_passatempos`: cruzada direta — dicas nas casas, numeração derivada. É
// por este estilo que o catálogo de passatempos reconhece a linha como autorável aqui.
const ESTILO_CRUZADA_DIRETA = 3;
// `tipos_de_passatempos`: "Cruzadas Diretas Diárias", o padrão natural do formulário.
const TIPO_CRUZADA_DIRETA = 3;

export const crosswordModule: RegisteredEditorModule<CrosswordDoc, CrosswordAction> = {
  kind: 'crossword',
  label: 'Cruzada direta',

  entry: { catalog: 'passatempo', estilosDePassatemposIds: [ESTILO_CRUZADA_DIRETA] },

  newPuzzle: {
    title: 'Nova cruzada direta',
    description: 'Uma cruzada direta nova — dicas nas casas escuras, numeração derivada.',
    notice: {
      message: 'Cruzada direta (de setas)',
      description:
        "A dica fica DENTRO da casa escura, apontando para onde a resposta começa — não há lista de dicas ao lado. É o formato de banca. (A cruzada tradicional, com dicas numa lista, é criada em '+ Novo jogo' → 'Cruzada tradicional', gerando automaticamente ou importando o XML.)",
    },
    titlePlaceholder: 'Ex.: Diretas fáceis',
    // O autor escolhe o tamanho; o 2..30 era o limite hardcoded no modal e agora é
    // declaração deste módulo (a cruzada é quem sempre teve essa opinião).
    dimensions: { mode: 'author', largura: 8, altura: 8, min: 2, max: 30 },
    tipo: { filter: isCrosswordTipo, defaultId: TIPO_CRUZADA_DIRETA },
  },

  createEmpty,
  initialUi: initialCrosswordUi,
  reduce: crosswordReducer,
  validate,
  coalesceKey: coalesceKeyFor,

  load: (id) => getCrosswordSource(id).then(sourceToDoc),

  save: (id, doc) => {
    const payload = docToDraftPayload(doc);
    return id == null ? createCrossword(payload) : replaceCrossword(id, payload);
  },

  preview: (doc) =>
    previewCrossword(docToDraftPayload(doc)).then((r) => ({ id: 0, indices: r.indices })),

  deriveIndices,
  duplicate: duplicatePassatempo,

  import: {
    kind: 'crossword-direct',
    toDoc: (result) => {
      if (result.kind !== 'crossword-direct') throw new Error(`Import de ${result.kind} na cruzada direta.`);
      return draftToDoc(result.draft, result.titulo, TIPO_CRUZADA_DIRETA);
    },
  },

  Canvas,
  Inspector,
};
